import { useCallback, useMemo, useState } from 'react';
import { FormikHelpers } from 'formik';
import type { Editor } from '@tiptap/react';

import {
  DEFAULT_FOOTER_SETTINGS,
  DEFAULT_HEADER_SETTINGS,
  DocumentSettings as DocumentSettingsType,
  DocumentViewMode,
  readDocumentSettings,
  toPagesCustomFormat,
  writeDocumentSettings,
} from 'containers/Tiptap/components/tiptap-extension/document-settings-yjs';
import { useEditorStore } from 'containers/Tiptap/hooks/useEditorStore';

import { DocumentSettingsFormValues } from './types';

export const getDefaultFormValues = (): DocumentSettingsFormValues => ({
  viewMode: DocumentViewMode.Pageless,
  pageSize: 'A4',
  orientation: 'portrait',
  margins: 'normal',
});

export const toFormValues = (settings: DocumentSettingsType): DocumentSettingsFormValues => ({
  viewMode: settings.viewMode,
  pageSize: settings.pageSize,
  orientation: settings.orientation,
  margins: settings.margins,
});

interface PendingFlip {
  values: DocumentSettingsFormValues;
  helpers: FormikHelpers<DocumentSettingsFormValues>;
}

const dispatchPageCommands = (editor: Editor | null, values: DocumentSettingsFormValues): void => {
  if (!editor) return;
  const commands = editor.commands as Record<string, unknown>;

  if (values.viewMode === DocumentViewMode.Pages && typeof commands.setPageFormat === 'function') {
    editor.commands.setPageFormat(
      toPagesCustomFormat({
        pageSize: values.pageSize,
        orientation: values.orientation,
        margins: values.margins,
      })
    );

    // Odd/even headers were removed; force the toggle off in case a document was saved with it on.
    if (typeof commands.setDifferentOddEven === 'function') {
      editor.commands.setDifferentOddEven(false);
    }
    if (typeof commands.setDifferentOddEvenFooter === 'function') {
      editor.commands.setDifferentOddEvenFooter(false);
    }
  }
};

export interface DocumentSettingsFormController {
  initialValues: DocumentSettingsFormValues;
  handleSubmit: (values: DocumentSettingsFormValues, helpers: FormikHelpers<DocumentSettingsFormValues>) => void;
  pendingFlip: PendingFlip | null;
  confirmFlip: () => void;
  cancelFlip: () => void;
}

export const useDocumentSettingsForm = (): DocumentSettingsFormController => {
  const editorStore = useEditorStore();
  const { editor, ydoc } = editorStore;

  const initialValues = useMemo(() => {
    const base = ydoc ? toFormValues(readDocumentSettings(ydoc)) : getDefaultFormValues();
    return {
      ...base,
      viewMode: editorStore.viewMode,
    };
  }, [ydoc, editorStore.viewMode]);

  const [pendingFlip, setPendingFlip] = useState<PendingFlip | null>(null);

  const applySettings = useCallback(
    (values: DocumentSettingsFormValues, helpers: FormikHelpers<DocumentSettingsFormValues>) => {
      if (!ydoc) {
        helpers.setStatus('The document is still connecting. Open the Document tab, then try again.');
        helpers.setSubmitting(false);
        return;
      }

      // Headers and footers, Different first page included, are edited from the header/footer panel.
      // The form only updates layout fields.
      const current = readDocumentSettings(ydoc);
      const currentHeader = current.header ?? DEFAULT_HEADER_SETTINGS;
      const currentFooter = current.footer ?? DEFAULT_FOOTER_SETTINGS;

      const zoneChanged = (zone: { show: boolean; locked: boolean }): boolean => !zone.show || zone.locked;

      writeDocumentSettings(ydoc, {
        viewMode: values.viewMode,
        pageSize: values.pageSize,
        orientation: values.orientation,
        margins: values.margins,
        ...(zoneChanged(currentHeader) && {
          header: { ...currentHeader, show: true, locked: false },
        }),
        ...(zoneChanged(currentFooter) && {
          footer: { ...currentFooter, show: true, locked: false },
        }),
      });

      editorStore.setViewMode(values.viewMode);
      dispatchPageCommands(editor, values);

      helpers.resetForm({ values });
      helpers.setSubmitting(false);
    },
    [editor, editorStore, ydoc]
  );

  const handleSubmit = useCallback(
    (values: DocumentSettingsFormValues, helpers: FormikHelpers<DocumentSettingsFormValues>) => {
      if (values.viewMode !== initialValues.viewMode) {
        setPendingFlip({ values, helpers });
        return;
      }
      applySettings(values, helpers);
    },
    [applySettings, initialValues.viewMode]
  );

  const confirmFlip = useCallback(() => {
    if (!pendingFlip) return;
    applySettings(pendingFlip.values, pendingFlip.helpers);
    setPendingFlip(null);
  }, [applySettings, pendingFlip]);

  const cancelFlip = useCallback(() => {
    if (pendingFlip) {
      pendingFlip.helpers.setSubmitting(false);
    }
    setPendingFlip(null);
  }, [pendingFlip]);

  return { initialValues, handleSubmit, pendingFlip, confirmFlip, cancelFlip };
};
